import React, { useState } from 'react';
import { X, Copy, Check, Share2, QrCode } from 'lucide-react';
import { TwitterXIcon, LinkedInIcon, FacebookIcon } from './SocialIcons';

export default function ShareModal({ isOpen, onClose, title = "Share this design", url = window.location.href }) {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.65)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px'
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-medium)',
        borderRadius: '24px',
        maxWidth: '460px',
        width: '100%',
        padding: '28px',
        boxShadow: 'var(--shadow-float)',
        animation: 'fadeInScale 0.2s ease'
      }} onClick={e => e.stopPropagation()}>
        
        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ padding: '8px', borderRadius: '10px', background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
              <Share2 size={18} />
            </div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800 }}>{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="tactile-btn"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* URL Copy Bar */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
            Direct Link
          </label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: 'var(--bg-base)',
            border: '1px solid var(--border-subtle)',
            borderRadius: '12px',
            padding: '4px 6px 4px 14px',
            gap: '8px'
          }}>
            <input
              type="text"
              readOnly
              value={url}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
                flex: 1,
                outline: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            />
            <button
              onClick={handleCopy}
              className="tactile-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                background: copied ? 'var(--accent)' : 'var(--primary)',
                color: copied ? '#000000' : '#FFFFFF',
                fontSize: '0.8125rem',
                fontWeight: 700,
                cursor: 'pointer',
                border: 'none'
              }}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Social Platforms & Actions */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '10px' }}>
            Share to Socials
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tactile-btn"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                padding: '12px 8px',
                borderRadius: '12px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              <TwitterXIcon size={18} />
              <span>X / Twitter</span>
            </a>

            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tactile-btn"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                padding: '12px 8px',
                borderRadius: '12px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              <LinkedInIcon size={18} color="#0A66C2" />
              <span>LinkedIn</span>
            </a>

            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tactile-btn"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                padding: '12px 8px',
                borderRadius: '12px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                fontSize: '0.75rem',
                fontWeight: 600
              }}
            >
              <FacebookIcon size={18} color="#1877F2" />
              <span>Facebook</span>
            </a>

            <button
              onClick={() => setShowQR(!showQR)}
              className="tactile-btn"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                padding: '12px 8px',
                borderRadius: '12px',
                background: showQR ? 'var(--primary-subtle)' : 'var(--bg-base)',
                border: `1px solid ${showQR ? 'var(--primary)' : 'var(--border-subtle)'}`,
                color: showQR ? 'var(--primary-text)' : 'var(--text-primary)',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <QrCode size={18} color="var(--primary)" />
              <span>QR Code</span>
            </button>
          </div>
        </div>

        {/* QR Code Preview */}
        {showQR && (
          <div style={{
            padding: '20px',
            background: 'var(--bg-base)',
            borderRadius: '16px',
            textAlign: 'center',
            border: '1px solid var(--border-subtle)',
            animation: 'fadeIn 0.2s ease'
          }}>
            <div style={{
              background: '#FFFFFF',
              padding: '16px',
              borderRadius: '12px',
              display: 'inline-block',
              marginBottom: '8px'
            }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedUrl}`}
                alt="QR Code"
                style={{ width: '130px', height: '130px', display: 'block' }}
              />
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Scan with mobile camera to view instantly</p>
          </div>
        )}
      </div>
    </div>
  );
}
